import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { animationConfigs } from '../animations.config';

import { Animation, AnimationType } from './animations.model';

// import { AnimationType, Animation } from 'src/app/extensions/dot-cms-uve/models/dot-cms-uve-contentlet.model';

gsap.registerPlugin(ScrollTrigger);

@Component({
  selector: 'dot-cms-animation',
  templateUrl: './dot-cms-animation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class DotCmsAnimationComponent implements AfterViewInit, OnChanges, OnInit {
  @Input() animation!: Animation | null;

  private activeMouseTween?: gsap.core.Tween & { cleanup?(): void };
  private element?: HTMLElement;

  constructor(private cdRef: ChangeDetectorRef, private ngZone: NgZone, private elementRef: ElementRef) {}
  ngOnInit() {}
  ngAfterViewInit() {
    this.element = this.elementRef.nativeElement.firstElementChild as HTMLElement;
    this.refreshAnimation();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.animation) {
      this.refreshAnimation();
    }
  }

  private refreshAnimation() {
    if (!this.element || !this.animation) {
      return;
    }

    // Cleanup previous mouse animation
    this.activeMouseTween?.cleanup?.();
    this.activeMouseTween?.kill();

    // Clear any GSAP tweens/styles
    gsap.killTweensOf(this.element);
    gsap.set(this.element, { clearProps: 'all' });

    this.ngZone.runOutsideAngular(() => {
      this.applyAnimation(this.animation!, this.element!);
    });
    this.ngZone.run(() => this.cdRef.markForCheck());
  }

  private applyAnimation(cfg: Animation, el: HTMLElement) {
    const { category, type, dynamicSettings } = cfg;

    if (category === 'mouse') {
      const fn = animationConfigs.mouse?.[type as AnimationType];
      if (!fn) {
        return;
      }
      this.activeMouseTween = fn(dynamicSettings, el, {});
      return;
    }

    const fn = animationConfigs[category]?.[type as AnimationType];
    if (!fn) {
      return;
    }

    const opts: any = {
      duration: dynamicSettings.duration ?? 0.8,
      ease: dynamicSettings.easing ?? 'power1.inOut',
      repeat: category === 'loop' ? -1 : 0,
      yoyo: category === 'loop',
      direction: dynamicSettings.direction,
      angle: dynamicSettings.angle,
    };

    if (category === 'scroll') {
      fn(dynamicSettings, el, {
        scrollTrigger: {
          trigger: el,
          start: dynamicSettings.start || 'top 80%',
          end: dynamicSettings.end || 'bottom 20%',
          toggleActions: dynamicSettings.toggleActions || 'play none none reverse',
          scrub: dynamicSettings.scrub ?? false,
          markers: dynamicSettings.markers ?? false,
        },
        ...opts,
      });
    } else {
      fn(dynamicSettings, el, opts);
    }
  }
}
